import React, { useEffect, useState, useRef, useContext } from "react";
import moment from "moment";
import PaymentInfo, { paymentMethod } from "./PaymentInfo";
import SelectServices from "./SelectServices";
import { useSelector, useDispatch } from "react-redux";
import { GlobalStore } from "../../reduxStore";
import ReservationTime from "./ReservationTime";
import VehicleResult from "../VehicleSearch/VehicleResult";
import styles from "./main.module.scss";
import {cx, isUnderTwentyOne} from "./util";
import { ScrollingPage } from "../MyReservations/ScrollingPage";
import { translateService, Translate } from "../../services/TranslateService";
import { User } from "../../interfaces/User";
import { baseOnSalesPrice, discountValue as calculateDiscountValue } from "./pricingCalculations";
import { basePrice } from './pricingCalculations';
import Modal from 'react-modal';
import LoginContainer from "../Login/LoginContainer";
import CustomerInfo from "./CustomerInfo";
import { Card } from "react-bootstrap";
import { DataContext } from "../../contexts/dataContext";
import { buildCart, GA4_CURRENCY } from "../../services/ga4Ecommerce";
import { firePendingPurchase, clearPendingPurchase } from "../../services/PurchaseGtagEvent";
import { AUTO_CUSTOMER_INFO_KEY } from "../../services/GoogleAnalyticsEvents";
import {Steps} from "../reservation-steps/steps";
import {checkResObjectDates} from "../CheckSearchTimesValid";
import PayCreatedReservation from "./PaymentForCreatedRes";
import PaymentSuccess from "./PaymentSuccess";
import PaymentFail from "./PaymentFail";
import {handleUrlWithHashtag, resetRrmUrl, scrollPageToTop, scrollToElementWithOffset} from "../../GetUrlParams";
import md5 from 'md5';
import {cStylesMakeRes} from "../../CustomModalStyles";
import ReservationCancelled from "./ReservationCancelled";
import {LoadingSpinner} from "../BusyModal/BusyModal";
import PaytrailProviderSelection from "./PaytrailProviderSelection";
import { parseRidSession } from "../../interfaces/RidSessionData";
import { Provider } from "../../interfaces/PaymentForm";
import { validateRedirectUrl } from "../../services/securityUtils";
// import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'; // TODO: re-enable when BE reCaptcha is ready

export const MakeReservation = ({ setRedirect, showPaymentResultScreen, setShowPaymentResultScreen}: any) => {
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const uiLanguage = useSelector((s: GlobalStore) => s.chauffeurReducer.deviceLanguage);
  const [detailsVisible, setDetailsVisible] = React.useState(-1);
  const dispatch = useDispatch();
  const [showLogin, setShowLogin] = useState(false);
  const [activeTab, setTab] = useState(1);
  const [totalPrice, setTotalPrice] = useState<number>();
  const [redirectHome, setRedirectHome] = useState(false);
  const topRef = useRef<HTMLSpanElement>(null);
  const dataContext = useContext(DataContext);
  const search = handleUrlWithHashtag();
  const reservationDays = dataContext.getReservationDays();
  const [escapeWarningOpen, setEscapeWarningOpen] = useState(false);
  const [safeDriveInfoSeen, setSafeDriveInfoSeen] = useState(false);
  const [showSafeDriveInfo, setShowSafeDriveInfo] = useState(dataContext.addSafeDrivePackage && !safeDriveInfoSeen);
  const [showPaymentInProgress, setShowPaymentInProgress] = useState(false);
  const paytrailCreateInFlight = useRef(false);
  const [paytrailProviders, setPaytrailProviders] = useState<Provider[] | null>(() => {
    try {
      const stored = sessionStorage.getItem('paytrailProviders');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  // const { executeRecaptcha } = useGoogleReCaptcha(); // TODO: re-enable when BE reCaptcha is ready
  const paytrailForForeigners = import.meta.env.VITE_ENABLE_PAYTRAIL_FOREIGNERS === 'true';
  let noPaymentResultScreen = !showPaymentResultScreen || showPaymentResultScreen === 'retry';

  function forgetUserSelections() {
    sessionStorage.setItem("route", "/");
    dataContext.clearSelections();
    dispatch({ type: "CLEAR_SELECTIONS", payload: null });
  }

  const returnToHome = () => {
    let urlLang = new URLSearchParams(search).get("lang") ?? undefined;
    forgetUserSelections();
    setRedirectHome(true);
    window.location.href = window.location.href.split("?")[0] +
        (urlLang && urlLang === 'en' ? '?lang=en' : '');
  }

  const clearPaytrailProviders = () => {
    sessionStorage.removeItem('paytrailProviders');
    setPaytrailProviders(null);
  }

  const handleBackToPaymentSelection = () => {
    clearPaytrailProviders();
    sessionStorage.removeItem('paymentProvider');
    sessionStorage.setItem('paytrailReselection', 'true');
  }

  useEffect(() => {
    const handlePageShow = (event: any) => {
      if (event.persisted) {
        // if bf cache on payment, reset to avoid old links/multiple payments on same res
        if (((sessionStorage.getItem("reservationCreated") ?? undefined) ||
                (sessionStorage.getItem('paymentSuccess') ?? undefined))
            && noPaymentResultScreen) {
          forgetUserSelections();
          setRedirectHome(true);
          resetRrmUrl(); // reset without reload
        }
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);


  useEffect(() => {
    scrollPageToTop();

    if (dataContext.page) {
      const parsedPage = JSON.parse(dataContext.page.toString());
      setTab(parsedPage);
    }

    let cp = sessionStorage.getItem("coupon");
    if (cp) {
      dataContext.changeStoredCoupon(JSON.parse(cp));
    }

    let res: any = sessionStorage.getItem("reservationCreated");
    if (res) {
      res = !!JSON.parse(res);
      dataContext.changeReservationCreated(res);
    }

    dataContext.changeLanguage(uiLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dataContext.vehicle) return;
    setTotalPrice(dataContext.vehicle.onsale ?
        baseOnSalesPrice(dataContext.vehicle, dataContext.noVatPrices, dataContext.services, reservationDays)
        :
        basePrice(dataContext.vehicle, dataContext.noVatPrices, dataContext.services, reservationDays)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext.services, dataContext.vehicle, reservationDays, dataContext.noVatPrices]);

  if (!totalPrice && dataContext.vehicle) {
    // this is only for makeReservations page reload, totalPrice becomes undefined
    setTotalPrice(dataContext.vehicle.onsale ?
        baseOnSalesPrice(dataContext.vehicle, dataContext.noVatPrices, dataContext.services, reservationDays)
        :
        basePrice(dataContext.vehicle, dataContext.noVatPrices, dataContext.services, reservationDays)
    );
  }

  const scrollToTop = (scrollLocation?: string) => {
    setTimeout(() => {
      if (scrollLocation) {
        const el = document.getElementById(scrollLocation);
        if (el) {
          scrollToElementWithOffset(el, 100);
          return;
        }
      }
      scrollPageToTop();
    }, 0);
  }

 const next = (data = null) => {
    if (dataContext.startDateInPast) {
      dataContext.openModifyDates();
      return;
    }
    sessionStorage.setItem("services", JSON.stringify(dataContext.services));
    sessionStorage.setItem("page", JSON.stringify(activeTab + 1));
    if (data) {
      dataContext.changeCustomer(data)
    }
    dataContext.changeRoute('/makereservation');
    dataContext.changePage(activeTab + 1);
    setTab((v: number) => v + 1);
    scrollToTop();
  }

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    dataContext.changePage(newTab);
    sessionStorage.setItem("page", JSON.stringify(newTab));
  };

  const back = (optOffset: undefined | number, scrollLocation?: string) => {
    const offset = optOffset ?? 1;
    sessionStorage.setItem("page", JSON.stringify(activeTab - offset));
    dataContext.changePage(activeTab - offset);
    setTab((v: number) => v - offset);
    scrollToTop(scrollLocation);
  }

  // ── Funnel parity for auto-signed-in customers ──────────────────────────────
  // Step 2 renders CustomerInfo only when `authHashLogin` is false. Launcher (and
  // chauffeur) users are signed in from the hash on the entry URL, so the customer
  // form is skipped entirely and `submit_customer_information` — fired from that
  // form's next() — never happened for them.
  //
  // The effect: any CLOSED GA4 funnel that includes that step silently dropped every
  // app user between "additional services" and "purchase", which reads as an app UX
  // collapse that does not exist. Fire the step explicitly when we skip the form, so
  // app and web funnels are shaped the same. `customer_info_source` keeps the two
  // distinguishable for anyone who needs to tell a real submit from an auto-fill.
  React.useEffect(() => {
    if (activeTab !== 2 || !dataContext.authHashLogin) return;
    if (sessionStorage.getItem(AUTO_CUSTOMER_INFO_KEY)) return;
    sessionStorage.setItem(AUTO_CUSTOMER_INFO_KEY, '1');
    dataContext.sendGa4Event("submit_customer_information", {
      customer_result: "success",
      customer_info_source: "auto_login",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataContext.authHashLogin]);

  let deviceInfo = sessionStorage.getItem('tt_appInfo') ?? undefined; // send app info in case res fails (for debugging)

  const paymentRedirect = async (e:any) => {
    if (dataContext.startDateInPast) {
      dataContext.openModifyDates();
      return;
    }
    // TODO: re-enable reCaptcha when BE is ready
    // let reCaptchaToken: string | undefined;
    // if (executeRecaptcha) {
    //   reCaptchaToken = await executeRecaptcha('create_reservation');
    // }
    let paymentMethodCode = dataContext.services.paymentMethod;
    dispatch({ // show loading
      type: 'SET_API_MESSAGE',
      payload: translateService.translate('vehicleSearch1.creatingReservation'),
    });
    let resDataObject = dataContext.reservation;
    // check if correct dates and times are set in res object
    let checkResDateTimes = checkResObjectDates(dataContext.searchParameters?.startDate ?? null, dataContext.searchParameters?.endDate ?? null,
        dataContext.reservation?.startDate, dataContext.reservation?.endDate, dataContext.reservation?.startTime, dataContext.reservation?.endTime)
    const searchVal = dataContext.searchParameters?.userPlaceSearch ?? dataContext.searchParameters?.pickupCity ?? '';
    const cityNameBeforeComma = searchVal.split(',')[0];
    const isForeignCustomer = !!(dataContext.customer?.unregisteredForeigner || dataContext.customer?.registeredForeigner);
    const usePaytrail = paytrailForForeigners && isForeignCustomer;

    // Payment reselection: user went back from PaytrailProviderSelection and picked
    // a new method. The reservation already exists — call create-paytrail with the
    // existing reservation ID so BE returns fresh providers without creating a new one.
    const isPaytrailReselection = sessionStorage.getItem('paytrailReselection') === 'true';
    if (isPaytrailReselection && dataContext.reservationCreated && usePaytrail) {
      sessionStorage.removeItem('paytrailReselection');
      const ridData = parseRidSession(sessionStorage.getItem('rid-1'));
      const resId = ridData?.resId;
      if (!resId) {
        setShowPaymentResultScreen('fail');
        dispatch({ type: 'SET_API_MESSAGE', payload: null });
        return;
      }
      let resolvedPaymentCode = paymentMethodCode;
      if (paymentMethodCode === paymentMethod.CREDIT_CARD_INPUT_VAL) {
        resolvedPaymentCode = dataContext.services.saveNewCreditCard
          ? paymentMethod.PAY_AND_STORE_CARD   // 200
          : paymentMethod.CREDIT_CARD_ONCE;    // 1
      }
      apiService?.reservation.createPaytrailReservation({
        reservationid: resId,
        paymentMethod: resolvedPaymentCode,
      }).then((response: { action?: string; providers?: Provider[]; url?: string; redirectUrl?: string; price?: number }) => {
        if (response.action === 'failed' || response.action === 'fail') {
          setShowPaymentResultScreen('fail');
          return;
        }
        if (response.action === 'paid') {
          sessionStorage.setItem('paymentPending', 'paid');
          sessionStorage.setItem('paymentProvider', 'paytrail');
          const secret = window.__env?.PAYMENT_HASH_SECRET || '';
          const hash = encodeURI(md5((resId ?? '') + secret));
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('paytrail', 'success');
          url.searchParams.set('h', hash);
          window.location.href = url.toString();
          return;
        }
        sessionStorage.setItem('paymentPending', 'true');
        sessionStorage.setItem('paymentProvider', 'paytrail');
        if (response.providers) {
          sessionStorage.setItem('paytrailProviders', JSON.stringify(response.providers));
          setPaytrailProviders(response.providers);
        } else if (response.url || response.redirectUrl) {
          const target = validateRedirectUrl((response.url || response.redirectUrl)!);
          if (target === '/') { setShowPaymentResultScreen('fail'); return; }
          setShowPaymentInProgress(true);
          window.location.href = target;
        } else {
          setShowPaymentResultScreen('fail');
        }
      }).catch(() => {
        setShowPaymentResultScreen('fail');
      }).finally(() => {
        dispatch({ type: 'SET_API_MESSAGE', payload: null });
      });
      return;
    }

    const resCreateSuccess = (resId: number) => {
      let resVehicle = dataContext.vehicle;
      dataContext.changeReservationCreated(resId);
      sessionStorage.setItem('rid-1', JSON.stringify({resId: resId, li: dataContext.loggedIn,
        model: resVehicle?.vehicleModelData.manufacturer + ' ' + resVehicle?.vehicleModelData.name + ' ' +
            (resVehicle?.vehicleModelData.dimensions.trunkVolume !== 0 ?
          resVehicle?.vehicleModelData.dimensions.trunkVolume + ' m³' : ''),
        startDate: dataContext.reservation.startDate + ' @ ' + dataContext.reservation.startTime,
        endDate: dataContext.reservation.endDate + ' @ ' + dataContext.reservation.endTime,
        lang: uiLanguage, num: dataContext.reservation.phoneNumber,
        pickupLoc: resVehicle?.homeLocationData.fullAddress,
        returnLoc: dataContext.oneWayRes ? resVehicle?.returnLocationData?.fullAddress : '',
        firstName: dataContext.customer?.givenName?.split(' ')[0] || "",
        b2b: ((dataContext.customer?.company && dataContext.customer.companyName !== '') || dataContext.customer?.companyVatNumber !== ''),
        // && dataContext.useCompanyInfo--> add when launcherMode has support for useCompany url param
        email: dataContext.reservation.email, linkedNum: dataContext.reservation.phoneNumber,
        locationSearch: !dataContext.oneWayRes ? cityNameBeforeComma : dataContext.searchParameters?.pickupCity,
        resCreateTime: moment().toISOString(),
        paymentProvider: usePaytrail ? 'paytrail' : 'walley',
        paymentMethod: paymentMethodCode === paymentMethod.CREDIT_CARD_INPUT_VAL
          ? (dataContext.services.saveNewCreditCard ? paymentMethod.PAY_AND_STORE_CARD : paymentMethod.CREDIT_CARD_ONCE)
          : paymentMethodCode}));
      sessionStorage.setItem("reservationCreated", "true");
      sessionStorage.setItem("resetDateTimes", '1');
      sessionStorage.setItem("paymentProvider", usePaytrail ? 'paytrail' : 'walley');
    }

    if (checkResDateTimes) {
      // if somehow res dates or times do not match verified search params, correct here by replacing...
      // with correct values to create res
      resDataObject = {...resDataObject, startDate: checkResDateTimes.startDate, startTime: checkResDateTimes.startTime,
        endDate: checkResDateTimes.endDate, endTime: checkResDateTimes.endTime
      }
    }

    // Paytrail foreign customers use the legacy create-paytrail endpoint which
    // both creates the reservation and returns provider buttons in one response.
    const createEndpoint = usePaytrail
      ? apiService?.reservation.createPaytrailReservation.bind(apiService.reservation)
      : apiService?.reservation.createReservation.bind(apiService.reservation);

    if (usePaytrail) paytrailCreateInFlight.current = true;
    createEndpoint?.({...resDataObject,
      device: deviceInfo ?? dataContext.mobileDevice ? 'mobile' : 'pc',
      /* reCaptchaToken */ }).then((response) => {
      let price = response.price;
      let resId = response.reservation;

      if (!resId) {
        setShowPaymentResultScreen('fail');
        return;
      }

      dataContext.sendPaymentGaEvent(price, resId);

      if (response.action === 'failed' || response.action === 'fail') {
        resCreateSuccess(resId);
        clearPendingPurchase();
        setShowPaymentResultScreen('fail');
        return;
      }

      const isInternalFlow = paymentMethodCode === paymentMethod.INVOICE
                          || paymentMethodCode === paymentMethod.QUOTA
                          || paymentMethodCode === paymentMethod.FREE;

      if (isInternalFlow) {
        sessionStorage.setItem('paymentPending', 'paid');
        resCreateSuccess(resId);
        const url = new URL(window.location.href);
        url.searchParams.set('r', String(resId));
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        setShowPaymentResultScreen('success');
        return;
      }

      if (paymentMethodCode === paymentMethod.STORED_CARD && apiService && !usePaytrail) {
        setShowPaymentInProgress(true);
        dataContext.sendGa4Event("payment_attempt", {
          payment_type: "stored_card",
          payment_result: "pending"
        });
        apiService.reservation.payWithSavedWalleyCard(resId)
          .then(() => {
            sessionStorage.setItem('paymentPending', 'paid');
            resCreateSuccess(resId);
            const url = new URL(window.location.href);
            url.searchParams.set('r', String(resId));
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
            setShowPaymentInProgress(false);
            setShowPaymentResultScreen('success');
            dataContext.sendGa4Event("payment_attempt", {
              payment_type: "stored_card",
              payment_result: "success"
            });
            firePendingPurchase(resId, dataContext.sendGa4Event);
          })
          .catch((err) => {
            resCreateSuccess(resId);
            setShowPaymentInProgress(false);
            if (dataContext.appContext !== "production") console.error('Saved card charge failed', err);
            setShowPaymentResultScreen('fail');
            dataContext.sendGa4Event("payment_attempt", {
              payment_type: "stored_card",
              payment_result: "failure",
              failure_stage: "charge"
            });
            clearPendingPurchase();
          });
        return;
      }

      if (usePaytrail) {
        resCreateSuccess(resId);
        dataContext.sendGa4Event("payment_attempt", {
          payment_type: "paytrail",
          payment_result: "pending"
        });
        if (response.action === 'paid') {
          sessionStorage.setItem('paymentPending', 'paid');
          const secret = window.__env?.PAYMENT_HASH_SECRET || '';
          const hash = encodeURI(md5((resId ?? '') + secret));
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('paytrail', 'success');
          url.searchParams.set('h', hash);
          window.location.href = url.toString();
          return;
        }
        sessionStorage.setItem('paymentPending', 'true');
        if (response.action === 'html' && response.providers) {
          sessionStorage.setItem('paytrailProviders', JSON.stringify(response.providers));
          setPaytrailProviders(response.providers);
        } else if (response.action === 'redirect' && response.redirectUrl) {
          const target = validateRedirectUrl(response.redirectUrl);
          if (target === '/') { setShowPaymentResultScreen('fail'); return; }
          setShowPaymentInProgress(true);
          window.location.href = target;
        } else if (response.providers) {
          sessionStorage.setItem('paytrailProviders', JSON.stringify(response.providers));
          setPaytrailProviders(response.providers);
        } else if (response.url || response.redirectUrl) {
          const target = validateRedirectUrl((response.url || response.redirectUrl)!);
          if (target === '/') { setShowPaymentResultScreen('fail'); return; }
          setShowPaymentInProgress(true);
          window.location.href = target;
        } else {
          setShowPaymentResultScreen('fail');
        }
        return;
      }

      // Walley iframe path. PayCreatedReservation will render off
      // reservationCreated + rid-1.resId and call /walley/checkout itself.
      sessionStorage.setItem('paymentPending', 'true');
      resCreateSuccess(resId);
    }).catch(e => {
      let errorMessage = 'unknown';
      if (dataContext.appContext !== "production") console.log(e);
      if (e.identifier === "API_ERROR_VEHICLE_NOT_AVAILABLE") {
        dispatch({
          type: 'SET_RESPONSE_MODAL',
          payload: translateService.translate('vehicleSearch1.notAvailable')
        });
        errorMessage = 'vehicle_not_available';
      } else {
        if(typeof e === 'string' && e.includes("INVALID_BODY_ERROR")) {
          let errorString = e;
          dispatch({
            type: "SET_RESPONSE_MODAL",
            payload: translateService.translate('profileCompletenessPhase.messages.savingFailed') +
                " (Reason for error:  " + errorString.slice(28, errorString.indexOf("}\"")) + ")",
          });
          errorMessage = 'invalid_body_error - (Reason for error:  ' + errorString.slice(28, errorString.indexOf("}\"")) + ")";
        } else {
          if(typeof e === 'string' && e.includes("INVALID_STATE_ERROR - VehicleNotAvailable")) {
            dispatch({
              type: "SET_RESPONSE_MODAL",
              payload: translateService.translate('profileCompletenessPhase.messages.savingFailed') +
                  " (" + translateService.translate('vehicleSearch1.reasonForError') + translateService.translate('vehicleSearch1.vehicleNotAvailableAnymore') + ")",
            });
            errorMessage = 'vehicle_not_available_anymore';
          } else {
            dispatch({
              type: "SET_RESPONSE_MODAL",
              payload: translateService.translate('profileCompletenessPhase.messages.savingFailed') + " (Reason for error:  " + e + ")",
            });
            errorMessage = 'unclassified - (Reason for error: ' + e + ")";
          }
        }
      }

      const failedInternalFlow = paymentMethodCode === paymentMethod.INVOICE
                              || paymentMethodCode === paymentMethod.QUOTA
                              || paymentMethodCode === paymentMethod.FREE;
      if (!failedInternalFlow) {
        dataContext.sendGa4Event("payment_attempt", {
            payment_type: usePaytrail ? "paytrail" : "walley",
            payment_result: "failure"
        });
      }

      if (!dataContext.reservationCreated) {
        dataContext.changeReservationCreated(false);
        sessionStorage.setItem("reservationCreated", "false");
        dataContext.sendGa4Event("create_reservation", {
          step: "confirm",
          reservation_result: "failure",
          rental_origin: "unknown",
          error_message: errorMessage
        });
      }      
    }).finally(() => {
      paytrailCreateInFlight.current = false;
      dispatch({
        type: 'SET_API_MESSAGE',
        payload: null,
      });
    });
  }

  useEffect(() => {
    if (!dataContext.vehicle && !totalPrice && dataContext.route === "/makereservation") {
      setRedirectHome(true);
    }
  }, [dataContext.vehicle, totalPrice]);

  useEffect(() => {
    // main safe drive service addition/removal effect
    if (dataContext.addSafeDrivePackage) {
      dataContext.changeServices({...dataContext.services, safeDrivePackage: true});
      if (!safeDriveInfoSeen && dataContext.route === "/makereservation") {
        setShowSafeDriveInfo(true);
      }
    } else if (dataContext.services?.safeDrivePackage) {
      dataContext.changeServices({...dataContext.services, safeDrivePackage: false});
    }

    if (!dataContext.addSafeDrivePackage && safeDriveInfoSeen) {
      // resets for overwriting user profile case on make res pages
      setShowSafeDriveInfo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext.addSafeDrivePackage, safeDriveInfoSeen]);

  // Re-fetch Paytrail providers when the payment screen needs them but they're
  // missing — happens after a Paytrail redirect (page reload clears React state)
  // or when PaymentFail's "Try again" clears showPaymentResultScreen.
  // Uses create-paytrail with {reservationid, paymentMethod} (same as old
  // PaymentRetry) so BE returns provider buttons instead of a legacy redirect URL.
  useEffect(() => {
    if (!noPaymentResultScreen) return;
    if (!dataContext.reservationCreated) return;
    if (sessionStorage.getItem('paymentProvider') !== 'paytrail') return;
    if (paytrailProviders) return;
    if (paytrailCreateInFlight.current) return;
    if (!apiService) return;

    const ridData = parseRidSession(sessionStorage.getItem('rid-1'));
    const resId = ridData?.resId;
    if (!resId) return;

    const storedMethod = Number(ridData.paymentMethod) || paymentMethod.PAYTRAIL;
    apiService.reservation.createPaytrailReservation({
      reservationid: resId,
      paymentMethod: storedMethod,
    })
      .then((resp: { action?: string; providers?: Provider[]; url?: string; redirectUrl?: string }) => {
        if (resp?.action === 'paid') {
          sessionStorage.setItem('paymentPending', 'paid');
          const secret = window.__env?.PAYMENT_HASH_SECRET || '';
          const hash = encodeURI(md5((resId ?? '') + secret));
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('paytrail', 'success');
          url.searchParams.set('h', hash);
          window.location.href = url.toString();
          return;
        }
        if (resp?.providers) {
          sessionStorage.setItem('paytrailProviders', JSON.stringify(resp.providers));
          setPaytrailProviders(resp.providers);
        } else if (resp?.action === 'redirect' && resp?.redirectUrl) {
          const target = validateRedirectUrl(resp.redirectUrl);
          if (target === '/') { setShowPaymentResultScreen('fail'); return; }
          setShowPaymentInProgress(true);
          window.location.href = target;
        } else {
          setShowPaymentResultScreen('fail');
        }
      })
      .catch(() => {
        setShowPaymentResultScreen('fail');
      });
  }, [noPaymentResultScreen, dataContext.reservationCreated, paytrailProviders, apiService]);

  const tabPage = (tab: number) => {
    switch (tab) {
      case 0:
        if (dataContext.vehicle?.availabilityData?.available)
          return <button onClick={() => {
            next()
          }}
                         className="btn btn-primary btn-outline-primary mt-2 w-100">
            <Translate content={'vehicleSearch.selectCar'} />
          </button>
        break;
      case 1:
        if (!dataContext.vehicle) {
          return (
              <div className={cx(styles.r24FullWidth, styles.r24TopMargin10)}>
                <div className={styles.r24FullWidth}>
                  Vehicle selection not found
                </div>
              </div>)
        } else {
          return (
              <SelectServices vehicle={dataContext.vehicle}
                              back={() => {setRedirectHome(true);
                                dataContext.sendGa4Event("navigate_back", { back_to: "to_search_results" })}}
                              next={() => {next();
                                if (dataContext.vehicle) {
                                  const cart = buildCart(dataContext.vehicle, dataContext.services, reservationDays, dataContext.noVatPrices);
                                  dataContext.sendGa4Event("add_to_cart", {
                                    currency: GA4_CURRENCY,
                                    value: cart.value,
                                    coupon: dataContext.storedCoupon?.code || undefined,
                                    items: cart.items
                                  });
                                }
                                dataContext.sendGa4Event("submit_additional_services", { services_result: "success"});}}
                              reservationLength={reservationDays}/>)
        }
      case 2:
        if (!dataContext.authHashLogin) {
          return <>
            <CustomerInfo setShowLogin={setShowLogin} next={() => {
              if(dataContext.customer && dataContext.vehicle){
                if(dataContext.customer.dateOfBirth !== '' ||  dataContext.customer.personalIdCode !== '' || dataContext.user?.birthday || dataContext.customer.loggedIn){
                  if(isUnderTwentyOne(dataContext.customer.dateOfBirth || dataContext.user?.birthday, dataContext.customer.personalIdCode)
                      && dataContext.vehicle && dataContext.vehicle.vehicleData.noundertwentyone){
                    dataContext.changeTwentyModal(true);
                  } else {
                    next();
                  }
                }
              }
              dataContext.sendGa4Event("submit_customer_information", { customer_result: "success"});
            }} back={back}
               openSafeDriveInfo={() => {
                 // note: consider onSales here #todo: check
                 let pricingDateType = dataContext.noVatPrices ? dataContext.vehicle?.pricingDataNoVat : dataContext.vehicle?.pricingData;
                 if (!pricingDateType?.base?.safeDrivePackage) return;
                 setShowSafeDriveInfo(true)}}
                          apiService={apiService}

            />
          </>
        } else {
          // Payment provider render gate: show the iframe/redirect screen whenever
          // the reservation has been created. Internal flows (FREE/QUOTA/INVOICE)
          // flip `showPaymentResultScreen` directly to 'success' from the
          // post-create handler, so they never reach this branch.
          if (dataContext.reservationCreated) {
            const provider = sessionStorage.getItem('paymentProvider');
            if (provider === 'paytrail') {
              if (paytrailProviders) {
                return <PaytrailProviderSelection
                    providers={paytrailProviders}
                    back={back}
                    setRedirectHome={setRedirectHome}
                    reservationLength={reservationDays}
                    escapeWarningOpen={escapeWarningOpen}
                    setEscapeWarningOpen={setEscapeWarningOpen}
                    openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                    setShowPaymentResultScreen={setShowPaymentResultScreen}
                    onBackToPaymentSelection={handleBackToPaymentSelection}
                />;
              }
              return <div className={cx(styles.r24FullWidth)} style={{minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <LoadingSpinner />
              </div>;
            }
            if (provider === 'walley') {
              return <PayCreatedReservation setRedirectHome={setRedirectHome}
                                            back={back} reservationLength={reservationDays}
                                            escapeWarningOpen={escapeWarningOpen}
                                            openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                                            setEscapeWarningOpen={setEscapeWarningOpen}
                                            setShowPaymentResultScreen={setShowPaymentResultScreen}
                                            apiService={apiService}
                                            showPaymentInProgress={showPaymentInProgress}
                                            setShowPaymentInProgress={setShowPaymentInProgress}
                                           />
            }
          }
          return <PaymentInfo setRedirectHome={setRedirectHome}
                              reservationCreated={dataContext.reservationCreated}
                              back={back} next={paymentRedirect} openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                              reservationLength={reservationDays}
                              showPaymentInProgress={showPaymentInProgress}
                              setShowPaymentInProgress={setShowPaymentInProgress}
                              escapeWarningOpen={escapeWarningOpen}
                              setEscapeWarningOpen={setEscapeWarningOpen}/>
        }
      case 3:
        // After reservation creation, the customer is on the payment screen.
        // Walley: re-renders PayCreatedReservation for a fresh /walley/checkout.
        // Paytrail: the customer was redirected off-page; if they return via
        // failure callback, PaymentFail handles retry.
        if (dataContext.reservationCreated) {
          const provider = sessionStorage.getItem('paymentProvider');
          if (provider === 'paytrail') {
            if (paytrailProviders) {
              return <PaytrailProviderSelection
                  providers={paytrailProviders}
                  back={back}
                  setRedirectHome={setRedirectHome}
                  reservationLength={reservationDays}
                  escapeWarningOpen={escapeWarningOpen}
                  setEscapeWarningOpen={setEscapeWarningOpen}
                  openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                  setShowPaymentResultScreen={setShowPaymentResultScreen}
                  onBackToPaymentSelection={handleBackToPaymentSelection}
              />;
            }
            return <div className={cx(styles.r24FullWidth)} style={{minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <LoadingSpinner />
            </div>;
          }
          if (provider === 'walley') {
            return <PayCreatedReservation setRedirectHome={setRedirectHome}
                                         back={back} reservationLength={reservationDays}
                                         escapeWarningOpen={escapeWarningOpen}
                                          openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                                          setEscapeWarningOpen={setEscapeWarningOpen}
                                          setShowPaymentResultScreen={setShowPaymentResultScreen}
                                          apiService={apiService}
                                          showPaymentInProgress={showPaymentInProgress}
                                          setShowPaymentInProgress={setShowPaymentInProgress}
                                          />
          }
        }
        return <PaymentInfo setRedirectHome={setRedirectHome}
                            reservationCreated={dataContext.reservationCreated}
                            back={back} next={paymentRedirect} openSafeDriveInfo={() => setShowSafeDriveInfo(true)}
                            reservationLength={reservationDays}
                            showPaymentInProgress={showPaymentInProgress}
                            setShowPaymentInProgress={setShowPaymentInProgress}
                            escapeWarningOpen={escapeWarningOpen}
                            setEscapeWarningOpen={setEscapeWarningOpen}/>
    }
  }

  const PaymentResultScreen = () => {
    if (showPaymentResultScreen === 'success') {
      return (
          <PaymentSuccess rootUrl={dataContext.rootUrl ?? ''}
                          returnToHome={() => {returnToHome()}}
                          changeVirtualPageInfo={dataContext.changeVirtualPageInfo}
                          mobileDevice={dataContext.mobileDevice}
                          apiService={apiService}
                          changeResCancelled={dataContext.changeResCancelled}/>)
    } else if (showPaymentResultScreen === 'cancelled') {
      return (
          <ReservationCancelled apiService={apiService}
                       pageType={showPaymentResultScreen} returnToHome={() => {returnToHome()}}
                       changeVirtualPageInfo={dataContext.changeVirtualPageInfo}/>
      )
    } else if (showPaymentResultScreen === 'vehicle_unavailable') {
      return (
          <div className='callback-info-container pb-3'>
            <div className='payment-callback-section py-3 mobile-top-line'>
              <div className="logo-heading-row">
                <img width="35" height="35" src={(dataContext.rootUrl ?? '') + '/assets/icons/warning-icon.png'} alt="Warning" loading="lazy" />
                <h1 className='payment-callback-heading text-center'>
                  {translateService.translate('paymentCallbackScreens.vehicleUnavailableHeading')}
                </h1>
              </div>
              <span className='text-center'>
                {translateService.translate('paymentCallbackScreens.vehicleUnavailableText')}
              </span>
              <div className='d-flex justify-content-center'>
                <button className="btn btn-primary btn-outline-primary btn-cta mt-3"
                        onClick={() => {
                          sessionStorage.removeItem('paymentSuccess');
                          sessionStorage.removeItem('rid-1');
                          sessionStorage.removeItem('paymentPending');
                          sessionStorage.removeItem('paytrailProviders');
                          sessionStorage.removeItem('paymentProvider');
                          sessionStorage.removeItem('reservationCreated');
                          sessionStorage.removeItem('selectedCar');
                          sessionStorage.removeItem('carIdChanged');
                          sessionStorage.removeItem('carIdReservedCheck');
                          sessionStorage.setItem('page', '0');
                          dataContext.changePage(0);
                          dataContext.changeRoute('/');
                          dataContext.changeSearchParameters({...dataContext.searchParameters, carId: null});
                          sessionStorage.setItem('carIdChanged', 'true');
                        }}>
                  {translateService.translate('paymentCallbackScreens.resCancelledBtn1')}
                </button>
              </div>
            </div>
          </div>
      )
    } else {
      return (
          <PaymentFail rootUrl={dataContext.rootUrl ?? ''} apiService={apiService}
                       pageType={showPaymentResultScreen} returnToHome={() => {returnToHome()}}
                       changeVirtualPageInfo={dataContext.changeVirtualPageInfo}
                       setShowPaymentResultScreen={setShowPaymentResultScreen}
                       clearPaytrailProviders={clearPaytrailProviders}/>
      )
    }
  }

  useEffect(() => {
    if(redirectHome) {
      dataContext.changeRoute('/');
      dataContext.changeSearchParameters({...dataContext.searchParameters, carId: null});
      sessionStorage.setItem('page', '0');
      dataContext.changePage(0);
      sessionStorage.setItem('carIdChanged', 'true'); // if vehicle id in url param --> this disables using url id afterwards
    }
  }, [redirectHome])

  return <>
    <ScrollingPage>
      <Modal isOpen={showPaymentInProgress} style={Object.assign(cStylesMakeRes)} contentLabel="Payment In Progress - State blocking">
        <>
          <h2>{translateService.translate('vehicleSearch.loading')}</h2>
          <LoadingSpinner />
        </>
      </Modal>
      {noPaymentResultScreen ?
          <div className={styles.vehicleSearch}>
              <Modal isOpen={showSafeDriveInfo} style={Object.assign(cStylesMakeRes)} contentLabel="Safe Drive Info">
                <div>
                  <h2>{translateService.translate('vehicleSearch1.safeDrivePackage')}</h2>
                  <p>
                    {translateService.translate('vehicleSearch1.safeDriveText1')}
                    <br/><br/>
                    {translateService.translate('vehicleSearch1.safeDriveText2')}
                  </p>
                  <button className="btn btn-primary btn-block btn-cta w-50 m-auto"
                          onClick={() => {setSafeDriveInfoSeen(true); setShowSafeDriveInfo(false)}}>
                    {translateService.translate('globalTexts.ok')}
                  </button>
                </div>
              </Modal>
          <Modal isOpen={showLogin} style={Object.assign(cStylesMakeRes)} contentLabel='Login'>
            <LoginContainer
                setUser={(user: User) => dataContext.changeUser(user)}
                customerInfoOpen={(dataContext.page === 2 && !dataContext.authHashLogin)}
                setIsLoggedIn={dataContext.changeLoggedIn}
                clearUserSelections={dataContext.clearSelections}
                setRedirect={() => {setShowLogin(false)}}
                sendGa4Event={dataContext.sendGa4Event}
                closeModal={() => setShowLogin(false)}
                appContext={dataContext.appContext ?? ''}
                rootUrl={dataContext.rootUrl ?? ''}
            />
          </Modal>
          <Steps onTabChange={handleTabChange} isPaymentPending={!!dataContext.reservationCreated}
                 setEscapeWarningOpen={setEscapeWarningOpen}/>
          {(dataContext.vehicle && totalPrice) ?
              (<div style={{ padding: "10px", maxWidth: "768px", minWidth: "320px", display: (dataContext.vehicle && totalPrice) ? "block" : "none" }}
                    className={cx(styles.r24FullWidth, styles.grayBorderBottom, styles.r24Padding10, styles.r24BottomMargin10)}>
                    <VehicleResult setRedirect={setRedirect} vehicle={dataContext.vehicle}
                                   stars={dataContext.stars ? dataContext.stars[dataContext.vehicle.id] : undefined}
                                   totalPrice={totalPrice} discountValue={calculateDiscountValue(dataContext.vehicle, dataContext.noVatPrices, dataContext.services, reservationDays)}
                                   detailsVisible={0 === detailsVisible} setDetailsVisible={() => setDetailsVisible(() => { return 0 === detailsVisible ? -1 : 0 })}
                                   displaySticky discount={null} selectVehicle={() => { }}
                                   noVat={dataContext.noVatPrices}
                    />
                    {!dataContext.searchParameters ?
                        <div className={styles.carListing}>
                          <Card className={`card-body mb-4`}> Reservation details not found </Card>
                        </div>
                        : <ReservationTime carId={dataContext.vehicle?.id}/>
                    }
                    <span ref={topRef} id="top" />
                    {tabPage(activeTab)}
                  </div>
              ) :
              null }
          {(!dataContext.vehicle && !totalPrice) ?
              (<div style={{padding: "10px", maxWidth: "768px", minWidth: "320px", display: (!dataContext.vehicle && !totalPrice) ? "block !important" : "none !important"}} className={cx(styles.r24FullWidth, styles.grayBorderBottom, styles.r24Padding10, styles.r24BottomMargin10)}>
                <h2 className="past-reservation-title my-3 text-center"> {translateService.translate('vehicleSearch.noVehicleSelectedOnReloadTitle')} </h2>
                <div className={styles.resultsLoaderDiv}>
                  <span className={styles.resultsLoader}>  </span>
                </div>
              </div>) : null}
        </div> :
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center"}}>
              <PaymentResultScreen />
            </div>
      }
    </ScrollingPage>
  </>;
}

export interface Customer {
  company: boolean,
  companyName: string,
  companyVatNumber: string,
  companyReference: string,
  familyName: string,
  givenName: string,
  unregisteredForeigner: boolean,
  registeredForeigner: boolean,
  personalIdCode: string,
  address: string,
  dateOfBirth: string,
  noBirthday: boolean,
  municipality: string,
  postalCode: string,
  country: string,
  secret: boolean,
  prepaid: boolean,
  phoneNumber: string,
  phoneNumber2: string,
  email: string,
  email2: string,
  additionalDriver: boolean,
  driverName: string,
  driverPhone: string,
  gdpr: boolean,
  agree: boolean,
  agree2: boolean,
  fairAccept: boolean,
  emailConsent: string,
  mayUseQuota: boolean,
  loggedIn: boolean //always true in chauffeur
  relativeBankTransferDueDate: number,
  mayUseBankTransferAnytime: boolean,
}